import { create } from 'zustand';
import type {
  ScenarioInput,
  ScenarioSummary,
  ComputeResponse,
  VehicleInput,
  LineItem,
} from '../types';
import { defaultScenarioInput, defaultVehicleInput, defaultLineItem } from '../types';
import * as api from '../utils/api';
import { demoScenario } from '../data/demoScenario';

export type AppMode = 'individual' | 'fleet';

interface ScenarioStore {
  // State
  scenarios: ScenarioSummary[];
  currentScenario: ScenarioInput;
  currentScenarioId: string | number | null;
  computeResults: ComputeResponse | null;
  loading: boolean;
  error: string | null;
  app_mode: AppMode;

  // Actions
  loadScenarios: () => Promise<void>;
  loadScenario: (id: string | number) => Promise<void>;
  saveScenario: () => Promise<string | number>;
  deleteScenario: (id: string | number) => Promise<void>;
  runCompute: () => Promise<void>;
  setCurrentScenario: (scenario: ScenarioInput) => void;
  patchScenario: (patch: Partial<ScenarioInput>) => void;
  setHorizonMonths: (months: number) => void;
  addVehicle: () => string;
  updateVehicle: (vehicleId: string, updates: Partial<VehicleInput>) => void;
  removeVehicle: (vehicleId: string) => void;
  duplicateVehicle: (vehicleId: string) => void;
  resetToNew: (mode?: AppMode) => void;
  clearError: () => void;
  importScenario: (json: ScenarioInput) => Promise<string | number>;
  seedDemoScenario: () => Promise<void>;
  /**
   * Phase 22.4: mode is locked at creation. setAppMode only mutates the
   * draft when there is no persisted scenarioId yet (i.e. /scenario/<mode>/new).
   * Once a scenario has been saved, mode is immutable; calls are ignored.
   */
  setAppMode: (mode: AppMode) => void;
  // Phase 22: heterogeneous fleet line items
  addLineItem: () => string;
  updateLineItem: (index: number, patch: Partial<LineItem>) => void;
  updateLineItemVehicle: (index: number, updates: Partial<VehicleInput>) => void;
  removeLineItem: (index: number) => void;
  duplicateLineItem: (index: number) => void;
  setFleetName: (name: string | null) => void;
}

export const useScenarioStore = create<ScenarioStore>((set, get) => ({
  scenarios: [],
  currentScenario: defaultScenarioInput(),
  currentScenarioId: null,
  computeResults: null,
  loading: false,
  error: null,
  app_mode: 'individual',

  loadScenarios: async () => {
    set({ loading: true, error: null });
    try {
      const scenarios = await api.getScenarios();
      set({ scenarios, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  loadScenario: async (id: string | number) => {
    set({ loading: true, error: null, computeResults: null });
    try {
      const detail = await api.getScenario(id);
      // Ensure loaded scenario has all required fields (backward compat with older data)
      const scenario = detail.scenario;
      // Phase 22.4: scenario.id is preserved on the detail wrapper, but the
      // editor needs to know the persisted id so mode-immutability can be enforced.
      if (!scenario.horizon_months) scenario.horizon_months = 60;
      if (!scenario.annual_miles) scenario.annual_miles = 12000;
      if (scenario.financial_weight == null) scenario.financial_weight = 0.70;
      if (scenario.emotional_weight == null) scenario.emotional_weight = 0.30;
      if (scenario.dominance_epsilon_pct == null) scenario.dominance_epsilon_pct = 0.25;
      if (scenario.use_emotional_override == null) scenario.use_emotional_override = scenario.dominance_epsilon_pct >= 0.999;
      if (scenario.investment_return_rate == null) scenario.investment_return_rate = 0.07;
      // Phase 22.7: scenario-level fuel-price defaults. Older saves did not
      // have these fields; default to null so the resolver chain falls
      // through to per-vehicle values exactly as before.
      if (scenario.scenario_fuel_cost_per_gallon === undefined)
        scenario.scenario_fuel_cost_per_gallon = null;
      if (scenario.scenario_diesel_price_per_gallon === undefined)
        scenario.scenario_diesel_price_per_gallon = null;
      if (scenario.scenario_electricity_rate_per_kwh === undefined)
        scenario.scenario_electricity_rate_per_kwh = null;
      // Migrate emotional scores to 1-5 scale
      for (const v of scenario.vehicles) {
        const s = v.emotional?.scores;
        if (s) {
          const vals = Object.values(s) as number[];
          if (vals.some((x) => x > 5)) {
            // Old 0-10 scale: divide by 2, clamp to min 1
            const keys = Object.keys(s) as (keyof typeof s)[];
            for (const k of keys) s[k] = Math.max(1, Math.round(s[k] / 2));
          } else {
            // Already 0-5 scale: clamp any 0s to 1
            const keys = Object.keys(s) as (keyof typeof s)[];
            for (const k of keys) if (s[k] < 1) s[k] = 1;
          }
        }
      }
      // Lift sales_tax_rate from first vehicle if missing at scenario level
      if (scenario.sales_tax_rate == null) {
        scenario.sales_tax_rate = scenario.vehicles[0]?.operating_costs?.sales_tax_rate ?? 0.06;
      }
      // Legacy scenarios saved before the round-3 backend fix had state_code
      // stripped at save time (Pydantic dropped the unknown field). Leave it
      // empty on load so the StateSelector shows its "Select a state…"
      // placeholder; the user re-picks once and the round-3 backend fix
      // persists the choice from there on. Silently defaulting to 'MI' here
      // would mask the loss and silently misprice CA / TX / etc. scenarios.
      if (!scenario.state_code) {
        scenario.state_code = '';
      }
      // Lift winter_miles_pct from first vehicle with winter tires if missing at scenario level
      if (scenario.winter_miles_pct == null) {
        const vw = scenario.vehicles.find((v) => v.operating_costs?.has_winter_tires);
        scenario.winter_miles_pct = vw?.operating_costs?.winter_miles_pct ?? 0.40;
      }
      // Phase 22: scenario mode + line items backfill
      if (scenario.mode !== 'fleet' && scenario.mode !== 'individual') {
        // Phase 22.4: infer mode from shape when missing/invalid. Multiple
        // line items, or any line item with quantity > 1, indicates fleet.
        const lis = scenario.line_items ?? [];
        const looksLikeFleet =
          lis.length > 1 ||
          lis.some((li) => Number(li.quantity ?? 1) > 1);
        scenario.mode = looksLikeFleet ? 'fleet' : 'individual';
        // eslint-disable-next-line no-console
        console.warn('[VehicleTCO] inferred scenario.mode =', scenario.mode, 'for scenario', detail.id);
      }
      if (scenario.fleet_name === undefined) scenario.fleet_name = null;
      // Phase 22.4: normalize cross-mode data corruption on load. An "individual"
      // scenario that somehow has fleet-shaped line items (qty > 1 or N > 1)
      // is bad data — drop the line items down to one quantity-1 entry to keep
      // the editor honest about its mode.
      if (scenario.mode === 'individual') {
        const lis = scenario.line_items ?? [];
        const corrupted =
          lis.length > 1 || lis.some((li) => Number(li.quantity ?? 1) > 1);
        if (corrupted) {
          // eslint-disable-next-line no-console
          console.warn(
            '[VehicleTCO] individual scenario had fleet-shaped line_items; trimming. id=',
            detail.id,
          );
          if (lis.length > 0) {
            scenario.line_items = [{ ...lis[0], quantity: 1 }];
          } else {
            scenario.line_items = null;
          }
        }
      }
      // Backfill Phase 21A fleet/operations fields on vehicles loaded from older scenarios.
      for (const v of scenario.vehicles) {
        if (v.upfit_cost == null) v.upfit_cost = 0;
        if (v.upfit_description == null) v.upfit_description = '';
        if (v.vocation_profile === undefined) v.vocation_profile = null;
        if (v.loaded_hourly_labor_rate == null) v.loaded_hourly_labor_rate = 0;
        if (v.weekly_hours == null) v.weekly_hours = 0;
        if (v.weeks_per_year == null) v.weeks_per_year = 50;
        if (v.operating_days_per_week == null) v.operating_days_per_week = 5;
        if (v.drivers_per_vehicle == null) v.drivers_per_vehicle = 1;
        if (v.ownership_period_years == null) v.ownership_period_years = 5.0;
        if (v.annual_miles_per_vehicle === undefined) v.annual_miles_per_vehicle = null;
        if (v.daily_idle_hours_override === undefined) v.daily_idle_hours_override = null;
        if (v.mpg_data_source === undefined) v.mpg_data_source = null;
        if (v.electricity_rate_per_kwh == null) v.electricity_rate_per_kwh = 0.16;
        if (v.diesel_price_per_gallon == null) v.diesel_price_per_gallon = 4.00;
        if (v.charging_infrastructure_cost == null) v.charging_infrastructure_cost = 0;
        if (v.purchase_incentives == null) v.purchase_incentives = 0;
        if (v.expected_residual_value === undefined) v.expected_residual_value = null;
        if (v.residual_data_source === undefined) v.residual_data_source = null;
        if (v.depreciation_curve_id === undefined) v.depreciation_curve_id = null;
      }
      // Phase 22: when fleet mode but no line_items persisted, lift vehicles into line items.
      if (scenario.mode === 'fleet' && (!scenario.line_items || scenario.line_items.length === 0)) {
        scenario.line_items = scenario.vehicles.map((v) => ({
          vehicle: v,
          quantity: 1,
          line_item_name: null,
        }));
      }
      // Adopt fleet mode if we loaded a fleet scenario, individual otherwise.
      const nextAppMode: AppMode = scenario.mode === 'fleet' ? 'fleet' : 'individual';
      set({
        currentScenario: scenario,
        currentScenarioId: detail.id,
        loading: false,
        app_mode: nextAppMode,
      });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  saveScenario: async () => {
    const { currentScenario, currentScenarioId, app_mode } = get();
    set({ loading: true, error: null });
    try {
      // Phase 22: in fleet mode, derive vehicles from line_items so the backend's
      // required min_length=1 vehicles field always has content.
      let payload: ScenarioInput = { ...currentScenario, mode: app_mode };
      if (app_mode === 'fleet') {
        const lis = currentScenario.line_items ?? [];
        if (lis.length === 0) throw new Error('Fleet scenario requires at least one line item.');
        payload.vehicles = lis.map((li) => li.vehicle);
      }
      let savedId: string | number;
      if (currentScenarioId !== null) {
        const res = await api.updateScenario(currentScenarioId, payload);
        savedId = res.id;
      } else {
        const res = await api.createScenario(payload);
        savedId = res.id;
        set({ currentScenarioId: savedId });
      }
      // Refresh the list
      const scenarios = await api.getScenarios();
      set({ scenarios, loading: false });
      return savedId;
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
      throw e;
    }
  },

  deleteScenario: async (id: string | number) => {
    set({ loading: true, error: null });
    try {
      await api.deleteScenario(id);
      const scenarios = await api.getScenarios();
      const { currentScenarioId } = get();
      if (currentScenarioId === id) {
        set({ currentScenario: defaultScenarioInput(), currentScenarioId: null, computeResults: null });
      }
      set({ scenarios, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  runCompute: async () => {
    const { currentScenario, app_mode } = get();
    set({ loading: true, error: null });
    try {
      // Inject scenario-level values into each vehicle before sending
      const scenarioMiles = currentScenario.annual_miles ?? 12000;
      const scenarioTaxRate = currentScenario.sales_tax_rate ?? 0.08;
      const scenarioWinterPct = currentScenario.winter_miles_pct ?? 0.40;
      const payload = {
        ...currentScenario,
        vehicles: currentScenario.vehicles.map((v) => {
          // Fleet mode: per-vehicle annual miles overrides scenario-wide value when set.
          const perVehicleMiles =
            app_mode === 'fleet' && v.annual_miles_per_vehicle != null && v.annual_miles_per_vehicle > 0
              ? v.annual_miles_per_vehicle
              : scenarioMiles;
          return {
            ...v,
            depreciation: {
              ...v.depreciation,
              annual_mileage: perVehicleMiles,
            },
            operating_costs: {
              ...v.operating_costs,
              sales_tax_rate: scenarioTaxRate,
              ...(v.operating_costs.has_winter_tires ? { winter_miles_pct: scenarioWinterPct } : {}),
            },
          };
        }),
      };
      const results = await api.computeScenario(payload);
      set({ computeResults: results, loading: false });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
      throw e;
    }
  },

  setCurrentScenario: (scenario: ScenarioInput) => {
    set({ currentScenario: scenario });
  },

  patchScenario: (patch: Partial<ScenarioInput>) => {
    const { currentScenario } = get();
    set({ currentScenario: { ...currentScenario, ...patch } });
  },

  setHorizonMonths: (months: number) => {
    const { currentScenario } = get();
    set({ currentScenario: { ...currentScenario, horizon_months: months } });
  },

  addVehicle: () => {
    const { currentScenario } = get();
    const vehicle = defaultVehicleInput();
    set({
      currentScenario: {
        ...currentScenario,
        vehicles: [...currentScenario.vehicles, vehicle],
      },
    });
    return vehicle.vehicle_id;
  },

  updateVehicle: (vehicleId: string, updates: Partial<VehicleInput>) => {
    const { currentScenario } = get();
    set({
      currentScenario: {
        ...currentScenario,
        vehicles: currentScenario.vehicles.map((v) =>
          v.vehicle_id === vehicleId ? { ...v, ...updates } : v,
        ),
      },
    });
  },

  removeVehicle: (vehicleId: string) => {
    const { currentScenario } = get();
    set({
      currentScenario: {
        ...currentScenario,
        vehicles: currentScenario.vehicles.filter((v) => v.vehicle_id !== vehicleId),
      },
    });
  },

  duplicateVehicle: (vehicleId: string) => {
    const { currentScenario } = get();
    const idx = currentScenario.vehicles.findIndex((v) => v.vehicle_id === vehicleId);
    if (idx === -1) return;
    const original = currentScenario.vehicles[idx];
    const copy: VehicleInput = {
      ...JSON.parse(JSON.stringify(original)),
      vehicle_id: crypto.randomUUID(),
      label: `${original.label} (Copy)`,
    };
    const newVehicles = [...currentScenario.vehicles];
    newVehicles.splice(idx + 1, 0, copy);
    set({
      currentScenario: {
        ...currentScenario,
        vehicles: newVehicles,
      },
    });
  },

  resetToNew: (mode: AppMode = 'individual') => {
    const draft = defaultScenarioInput();
    draft.mode = mode;
    if (mode === 'fleet') {
      draft.fleet_name = 'New Fleet';
      draft.line_items = [defaultLineItem()];
    }
    set({
      currentScenario: draft,
      currentScenarioId: null,
      computeResults: null,
      error: null,
      app_mode: mode,
    });
  },

  clearError: () => set({ error: null }),

  setAppMode: (mode: AppMode) => {
    // Phase 22.4: mode is immutable after the scenario has been persisted.
    // For unsaved drafts (currentScenarioId === null), we still allow flipping
    // the in-memory shape so the editor can pre-seed correctly when the user
    // navigates between /scenario/individual/new and /scenario/fleet/new.
    const { currentScenario, currentScenarioId } = get();
    if (currentScenarioId !== null) {
      // eslint-disable-next-line no-console
      console.warn('[VehicleTCO] setAppMode ignored — mode is immutable after creation');
      return;
    }
    if (currentScenario.mode === mode) {
      set({ app_mode: mode });
      return;
    }
    const nextScenario: ScenarioInput = { ...currentScenario, mode };
    if (mode === 'fleet' && (!nextScenario.line_items || nextScenario.line_items.length === 0)) {
      nextScenario.line_items =
        currentScenario.vehicles.length > 0
          ? currentScenario.vehicles.map((v) => ({ vehicle: v, quantity: 1, line_item_name: null }))
          : [defaultLineItem()];
    }
    if (mode === 'fleet' && !nextScenario.fleet_name) {
      nextScenario.fleet_name = currentScenario.name || 'New Fleet';
    }
    if (mode === 'individual') {
      nextScenario.line_items = null;
    }
    set({ app_mode: mode, currentScenario: nextScenario });
  },

  addLineItem: () => {
    const { currentScenario } = get();
    const li = defaultLineItem();
    const lis = currentScenario.line_items ?? [];
    set({
      currentScenario: { ...currentScenario, line_items: [...lis, li] },
    });
    return li.vehicle.vehicle_id;
  },

  updateLineItem: (index, patch) => {
    const { currentScenario } = get();
    const lis = currentScenario.line_items ?? [];
    if (index < 0 || index >= lis.length) return;
    const next = lis.map((li, i) => (i === index ? { ...li, ...patch } : li));
    set({ currentScenario: { ...currentScenario, line_items: next } });
  },

  updateLineItemVehicle: (index, updates) => {
    const { currentScenario } = get();
    const lis = currentScenario.line_items ?? [];
    if (index < 0 || index >= lis.length) return;
    const next = lis.map((li, i) =>
      i === index ? { ...li, vehicle: { ...li.vehicle, ...updates } } : li,
    );
    set({ currentScenario: { ...currentScenario, line_items: next } });
  },

  removeLineItem: (index) => {
    const { currentScenario } = get();
    const lis = currentScenario.line_items ?? [];
    if (index < 0 || index >= lis.length) return;
    set({
      currentScenario: {
        ...currentScenario,
        line_items: lis.filter((_, i) => i !== index),
      },
    });
  },

  duplicateLineItem: (index) => {
    const { currentScenario } = get();
    const lis = currentScenario.line_items ?? [];
    if (index < 0 || index >= lis.length) return;
    const original = lis[index];
    const copy: LineItem = {
      ...JSON.parse(JSON.stringify(original)),
      vehicle: {
        ...JSON.parse(JSON.stringify(original.vehicle)),
        vehicle_id: crypto.randomUUID(),
      },
    };
    const next = [...lis];
    next.splice(index + 1, 0, copy);
    set({ currentScenario: { ...currentScenario, line_items: next } });
  },

  setFleetName: (name) => {
    const { currentScenario } = get();
    set({ currentScenario: { ...currentScenario, fleet_name: name } });
  },

  seedDemoScenario: async () => {
    // Guard against double-mount race condition (React Strict Mode or fast re-renders)
    if ((useScenarioStore as unknown as { _seeding?: boolean })._seeding) return;
    (useScenarioStore as unknown as { _seeding: boolean })._seeding = true;
    try {
      const existing = await api.getScenarios();
      if (existing.length > 0) return;
      await api.createScenario(demoScenario);
      const scenarios = await api.getScenarios();
      set({ scenarios });
    } catch {
      // Silently ignore seeding errors — non-critical
    } finally {
      (useScenarioStore as unknown as { _seeding: boolean })._seeding = false;
    }
  },

  importScenario: async (json: ScenarioInput) => {
    set({ loading: true, error: null, computeResults: null });
    try {
      // Sync scenario-level fields into each vehicle's operating_costs so they survive
      // the backend round-trip (backend ScenarioInput has no top-level sales_tax_rate
      // or winter_miles_pct; those live per-vehicle in OperatingCostsInput).
      const scenarioTaxRate = json.sales_tax_rate ?? 0.08;
      const scenarioWinterPct = json.winter_miles_pct ?? 0.40;
      const investmentRate = json.investment_return_rate ?? 0.07;

      const synced: ScenarioInput = {
        ...json,
        horizon_months: json.horizon_months || 60,
        annual_miles: json.annual_miles || 12000,
        financial_weight: json.financial_weight ?? 0.70,
        emotional_weight: json.emotional_weight ?? 0.30,
        dominance_epsilon_pct: json.dominance_epsilon_pct ?? 0.25,
        use_emotional_override: json.use_emotional_override ?? false,
        investment_return_rate: investmentRate,
        sales_tax_rate: scenarioTaxRate,
        winter_miles_pct: scenarioWinterPct,
        vehicles: (json.vehicles ?? []).map((v) => ({
          ...v,
          operating_costs: {
            ...v.operating_costs,
            sales_tax_rate: scenarioTaxRate,
            ...(v.operating_costs?.has_winter_tires ? { winter_miles_pct: scenarioWinterPct } : {}),
          },
        })),
      };

      const res = await api.createScenario(synced);
      const scenarios = await api.getScenarios();
      set({
        currentScenario: synced,
        currentScenarioId: res.id,
        scenarios,
        loading: false,
      });
      return res.id;
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
      throw e;
    }
  },
}));
